import { api } from "../api/Middleware";

// =============================
// PUBLIC AGENT SERVICES
// =============================

// Configurar URLs dos endpoints públicos
const PUBLIC_AGENT_ENDPOINTS = {
  info: (token) => `/agents/public/info/${token}/`,
  turnstileConfig: '/agents/public/turnstile-config/',
  validate: (token) => `/agents/public/validate/${token}/`,
  chat: (token, uid) => `/agents/public/chat/${token}/?uid=${uid}`,
  reset: (token) => `/agents/public/reset/${token}/`,
};

// Obter informações básicas do agente pelo token
export const getAgentInfo = async (token) => {
  try {
    console.log('=== DEBUG GET AGENT INFO ===');
    console.log('Token:', token);
    
    if (!token) {
      throw new Error('Token é obrigatório');
    }
    
    const response = await api.get(PUBLIC_AGENT_ENDPOINTS.info(token));
    
    console.log('Agent info response:', response.data);
    
    return {
      success: true,
      data: response.data,
      agent: response.data.agent || response.data,
      isValid: true,
    };
  } catch (error) {
    console.error('Erro ao obter informações do agente:', error);
    
    return {
      success: false,
      error: error.response?.data?.message || error.response?.data?.error || 'Agente não encontrado',
      errorCode: error.response?.data?.error_code || 'AGENT_NOT_FOUND',
      isValid: false,
    };
  }
};

// Obter configuração do Turnstile
export const getTurnstileConfig = async () => {
  try {
    console.log('=== DEBUG GET TURNSTILE CONFIG ===');
    
    const response = await api.get(PUBLIC_AGENT_ENDPOINTS.turnstileConfig);
    
    console.log('Turnstile config response:', response.data);
    
    return {
      success: true,
      data: response.data,
      config: response.data.config || response.data,
    };
  } catch (error) {
    console.error('Erro ao obter configuração do Turnstile:', error);
    
    return {
      success: false,
      error: error.response?.data?.message || error.response?.data?.error || 'Erro ao carregar configuração de segurança',
      errorCode: error.response?.data?.error_code || 'TURNSTILE_CONFIG_ERROR',
      config: null,
    };
  }
};

// Validar acesso com telefone e Turnstile
export const validateAccess = async (token, data) => {
  try {
    console.log('=== DEBUG VALIDATE ACCESS ===');
    console.log('Token:', token);
    console.log('Data:', data);
    
    if (!token) {
      throw new Error('Token é obrigatório');
    }
    
    if (!data.turnstileToken) {
      throw new Error('Token de segurança é obrigatório');
    }

    const requestData = {
      turnstile_token: data.turnstileToken,
    };
    
    const response = await api.post(PUBLIC_AGENT_ENDPOINTS.validate(token), requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 15000,
    });
    
    console.log('Validation response:', response.data);
    
    return {
      success: true,
      data: response.data,
      sessionToken: response.data.session_token || response.data.access_token,
      message: response.data.message || 'Acesso validado com sucesso',
    };
  } catch (error) {
    console.error('Erro ao validar acesso público:', error);
    
    let errorMessage = 'Erro ao validar acesso';
    let errorCode = 'VALIDATION_ERROR';
    
    if (error.response) {
      const { status, data } = error.response;
      errorMessage = data?.message || data?.error || errorMessage;
      errorCode = data?.error_code || errorCode;
      
      // Mapear códigos de status para mensagens específicas
      switch (status) {
        case 400:
          if (errorCode === 'PHONE_NOT_AUTHORIZED') {
            errorMessage = 'Telefone não autorizado para teste deste agente';
          } else if (errorCode === 'TURNSTILE_FAILED') {
            errorMessage = 'Falha na verificação de segurança. Tente novamente';
          }
          break;
        case 403:
          errorMessage = 'Acesso negado ao agente';
          break;
        case 404:
          errorMessage = 'Link de teste não encontrado ou expirado';
          break;
        case 429:
          errorMessage = 'Muitas tentativas. Aguarde alguns minutos';
          break;
      }
    }
    
    return {
      success: false,
      error: errorMessage,
      errorCode: errorCode,
    };
  }
};

// Enviar mensagem para o agente (texto ou áudio)
export const sendChatMessage = async (token, messageData) => {
  try {
    console.log('=== DEBUG SEND CHAT MESSAGE ===');
    console.log('Token:', token);
    console.log('Message data:', messageData);
    
    if (!token) {
      throw new Error('Token é obrigatório');
    }
    
    const isFormData = messageData instanceof FormData;
    
    let requestData;
    let requestHeaders = {};
    
    // Para APIs públicas, não enviar Authorization headers conforme documentação
    if (!isFormData) {
      requestHeaders['Content-Type'] = 'application/json';
    }
    
    if (isFormData) {
      // Para áudio, o FormData já está preparado
      requestData = messageData;
      // Para FormData, não definir Content-Type (deixar o browser definir com boundary)
      // conforme documentação da API
    } else {
      // Para mensagens de texto, validar se mensagem está presente
      if (!messageData.message) {
        throw new Error('Mensagem é obrigatória');
      }
      
      // Normalizar telefone para apenas números conforme API
      const normalizedPhone = messageData.phone ? messageData.phone.replace(/\D/g, '') : messageData.phone;

      requestData = {
        phone: normalizedPhone,
        message: messageData.message,
        session_token: messageData.sessionToken,
      };
    }

    let uid = window.location.search.includes("uid=")
      ? window.location.search.split("=")[1]
      : "";
    
    const requestConfig = { timeout: 30000 };
    // Para FormData (áudio), NÃO definir headers — deixar axios gerar Content-Type com boundary
    // Para JSON (texto), definir Content-Type explicitamente
    if (!isFormData) {
      requestConfig.headers = requestHeaders;
    }

    const response = await api.post(
      PUBLIC_AGENT_ENDPOINTS.chat(token, uid),
      requestData,
      requestConfig
    );
    
    console.log('Chat response:', response.data);
    
    // Normalizar resposta para ambos os formatos (legado e agents_flow)
    const resData = response.data;
    // Detecta o formato agents_flow pela presença das chaves próprias do fluxo.
    // IMPORTANTE: agent_response pode vir como string vazia ("") quando a
    // resposta é composta apenas de mídia (ex.: PDF) — nesses casos o conteúdo
    // real está em messages[]. Por isso não dá para usar !!agent_response.
    const isAgentFlow =
      resData.agent_response !== undefined || Array.isArray(resData.messages);

    return {
      success: true,
      status: response.status,
      data: isAgentFlow ? resData.agent_response : (resData.data || resData.response || resData.message || resData),
      messages: isAgentFlow ? (resData.messages || []) : [],
      isAgentFlow,
      messageId: resData.message_id,
    };
  } catch (error) {
    console.error('Erro ao enviar mensagem:', error);
    
    // Manter a estrutura de erro original para compatibilidade
    const errorResponse = {
      response: error.response,
      message: error.response?.data?.message || error.message || 'Erro ao enviar mensagem',
      code: error.response?.data?.error_code || 'MESSAGE_ERROR',
    };
    
    throw errorResponse;
  }
};

// Resetar conversa do agente
export const resetConversation = async (token, data) => {
  try {
    console.log('=== DEBUG RESET CONVERSATION ===');
    console.log('Token:', token);
    console.log('Data:', data);
    
    if (!token) {
      throw new Error('Token é obrigatório');
    }
    
    if (!data.phone) {
      throw new Error('Telefone é obrigatório');
    }
    
    // Normalizar telefone para apenas números conforme API
    const normalizedPhone = data.phone.replace(/\D/g, '');
    
    const requestData = {
      phone: normalizedPhone,
      session_token: data.sessionToken,
    };
    
    const response = await api.post(PUBLIC_AGENT_ENDPOINTS.reset(token), requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });
    
    console.log('Reset response:', response.data);
    
    return {
      success: true,
      data: response.data,
      message: response.data.message || 'Conversa resetada com sucesso',
    };
  } catch (error) {
    console.error('Erro ao resetar conversa:', error);
    
    return {
      success: false,
      error: error.response?.data?.message || error.response?.data?.error || 'Erro ao resetar conversa',
      errorCode: error.response?.data?.error_code || 'RESET_ERROR',
    };
  }
};

// =============================
// UTILITY FUNCTIONS
// =============================

// Processar erros da API pública
export const processPublicAgentError = (error) => {
  const errorData = error.response?.data || {};
  const status = error.response?.status;
  
  const errorTitle = errorData.error || 'Erro de comunicação';
  const errorMessage = errorData.message || 'Ocorreu um erro inesperado';
  const errorCode = errorData.error_code;
  
  let errorInfo = {
    type: 'generic',
    title: errorTitle,
    message: errorMessage,
    code: errorCode,
    status: status,
  };
  
  // Mapear códigos de erro específicos
  if (errorCode) {
    switch (errorCode) {
      case 'AGENT_NOT_FOUND':
      case 'INVALID_TOKEN':
        errorInfo.type = 'not_found';
        break;
      case 'PHONE_NOT_AUTHORIZED':
        errorInfo.type = 'unauthorized_phone';
        break;
      case 'TURNSTILE_FAILED':
        errorInfo.type = 'security_failed';
        break;
      case 'AGENT_OFFLINE':
        errorInfo.type = 'agent_offline';
        break;
      case 'RATE_LIMITED':
        errorInfo.type = 'rate_limited';
        break;
      case 'SESSION_EXPIRED':
        errorInfo.type = 'session_expired';
        break;
      default:
        errorInfo.type = 'generic';
        break;
    }
  } else {
    // Mapear por status HTTP
    switch (status) {
      case 400:
        errorInfo.type = 'validation';
        break;
      case 401:
      case 403:
        errorInfo.type = 'unauthorized';
        break;
      case 404:
        errorInfo.type = 'not_found';
        break;
      case 429:
        errorInfo.type = 'rate_limited';
        break;
      case 500:
        errorInfo.type = 'server_error';
        break;
      default:
        errorInfo.type = 'generic';
    }
  }
  
  return errorInfo;
};

// Validar telefone brasileiro
export const validateBrazilianPhone = (phone) => {
  const errors = [];
  
  if (!phone) {
    errors.push('Telefone é obrigatório');
    return { isValid: false, errors };
  }
  
  const cleanPhone = phone.replace(/\D/g, '');
  
  if (cleanPhone.length < 10 || cleanPhone.length > 11) {
    errors.push('Telefone deve ter 10 ou 11 dígitos');
  }
  
  if (cleanPhone.length === 11) {
    const validDDDs = [
      '11', '12', '13', '14', '15', '16', '17', '18', '19',
      '21', '22', '24', '27', '28',
      '31', '32', '33', '34', '35', '37', '38',
      '41', '42', '43', '44', '45', '46', '47', '48', '49',
      '51', '53', '54', '55',
      '61', '62', '63', '64', '65', '66', '67', '68', '69',
      '71', '73', '74', '75', '77', '79',
      '81', '82', '83', '84', '85', '86', '87', '88', '89',
      '91', '92', '93', '94', '95', '96', '97', '98', '99'
    ];
    
    const ddd = cleanPhone.substring(0, 2);
    if (!validDDDs.includes(ddd)) {
      errors.push('DDD inválido');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    cleanPhone,
  };
};

// Formatar telefone brasileiro
export const formatBrazilianPhone = (phone) => {
  const cleanPhone = phone.replace(/\D/g, '');
  
  if (cleanPhone.length <= 10) {
    return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  } else {
    return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
};

// =============================
// EXPORTS
// =============================

// Exportar funções principais
const PublicAgentService = {
  getAgentInfo,
  getTurnstileConfig,
  validateAccess,
  sendChatMessage,
  resetConversation,
  processPublicAgentError,
  validateBrazilianPhone,
  formatBrazilianPhone,
};

export default PublicAgentService;